import ComingSoon from "@/components/homepage/ComingSoon";
import { RoundCard } from "@/components/homepage/RoundCard";
import { MustReadTitle } from "@/components/rounds/Descriptions";
import { Prize } from "@/components/rounds/Prize";
import { ScoringList } from "@/components/rounds/ScoringList";
import SelectDetailTab from "@/components/rounds/SelectDetailTab";
import { Button } from "@/components/ui/button";
import { useHandleError } from "@/hooks/useHandleError";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import type { Round } from "@roman/shared/types/Round";
import { cn } from "@roman/shared/utils";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ALL_PLAYER_SCORE, DEFENDER_SCORE, GOAL_KEEPER_SCORE, NON_GK_SCORE, TABS, type Tab } from "./constant";

export const RoundPage = () => {
  return (
    <div className="w-full">
      <Rounds />
    </div>
  );
};

const Rounds = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roundId } = useParams<{ roundId: string }>();
  if (!roundId) {
    throw new Error("roundId is required");
  }

  const roundDetailHandleError = useHandleError("ラウンドの詳細取得に失敗しました");

  const { data: roundData, isLoading: isRoundLoading } = useSoccerRound(roundId, roundDetailHandleError, "ja");
  const [selectedTab, setSelectedTab] = useState<Tab>(TABS[0]);

  const selectedRound = useMemo(() => {
    if (!roundData?.round) {
      return null;
    }

    const round: Round = {
      ...roundData.round.roundEntryUnits[0],
      ...roundData.round,
    };
    return round;
  }, [roundData]);

  const ScoringLists = () => {
    return (
      <div>
        <ScoringList listName="全ポジション" scoring={ALL_PLAYER_SCORE} />
        <ScoringList listName="FW、MF、DF（GKを除く全選手）" scoring={NON_GK_SCORE} />
        <ScoringList listName="DF" scoring={DEFENDER_SCORE} />
        <ScoringList listName="GK" scoring={GOAL_KEEPER_SCORE} />
      </div>
    );
  };

  const handleGotoSelectPlayers = () => {
    if (!selectedRound) {
      return;
    }

    if (selectedRound.status === "Open" && selectedRound.entryCloseTime > Date.now()) {
      navigate(`/rounds/${roundId}/select-players`);
    }
  };

  const RoundDetailPlayButton = ({
    roundDetailPlayButtonText,
    handleGotoSelectPlayers,
  }: {
    roundDetailPlayButtonText: string | undefined;
    handleGotoSelectPlayers: () => void;
  }) => {
    return (
      <>
        {roundDetailPlayButtonText && (
          <Button
            variant="default"
            className="w-full h-[40px] font-bold text-base text-white text-center content-center rounded-[4px] mt-[20px] mb-[10px] text-[16px]"
            style={{
              background: "linear-gradient(91deg, #01A4F9 0%, #176BF2 100%)",
            }}
            onClick={handleGotoSelectPlayers}
          >
            {roundDetailPlayButtonText}
          </Button>
        )}
      </>
    );
  };

  const SelectedContent = ({ round }: { round: Round }) => {
    switch (selectedTab.type) {
      case "info":
        return (
          <div>
            <div className="py-8">
              <MustReadTitle title="大会の注意事項" />
              <RoundInfoText />

              <p className="text-sm text-dark-gray mt-4">
                より詳しい大会のルールについては、
                <a
                  href="https://half-pressure-b6c.notion.site/1b1941dfbe4080149e31cdc1c4e6cbfc"
                  className="text-secondary underline"
                >
                  こちらの規約
                </a>
                をご参照ください。
              </p>
            </div>
          </div>
        );
      case "rule": {
        const howToPlayText = [
          {
            title: "ファンタジースポーツの遊び方",
            text: [
              "参加者は、試合開始までに、AまたはBの2つの選択肢から選手を選び、自分だけのチームを作成します。",
              "さらに、選んだ選手からキャプテン（獲得ポイント2倍）や副キャプテン（獲得ポイント1.5倍）を選びます。",
              "選手5人または11人の活躍ポイント(FP：Fantasy Point)合計点で順位を決定し、上位者から賞金が配布されるPVPゲームです。",
            ],
            textClassName: "list-decimal",
          },
          {
            title: "活躍ポイント（FP：Fantasy Point）とは?",
            text: [
              "選手の試合でのパフォーマンスに基づいて計算されるポイントです。",
              "下記の、ゴールやアシスト、シュート成功などでポイントが加算され、ファウルや失点などのミスで減点されます。",
            ],
            textClassName: "list-none pl-0",
          },
        ];

        const evaluationText = {
          title: "選手の評価方法",
          text: [
            "「枠内シュート」は「シュート」としてもカウントされ、合計で+2ポイントとなります。",
            "「正確なパス」は、意図的に味方へ出されたパスを指します。",
            "「決定機パス」は、ゴールへのシュートにつながる最終パスを指します。",
            "選手がイエローカードを2枚受けても「レッドカード」のポイント換算はされず、選手のFPへの影響は-3ポイントとなります。",
            "ゴールキーパーの「勝利」は、延長戦終了時のスコアで決まり、PK戦は考慮されません。",
            "PKセーブ（PK戦を除く）は+2ポイント「セーブ」に加え、さらに+3ポイント「PKセーブ」を獲得します。",
            "オウンゴールは「ゴール」にはカウントされませんが、GKにとっては失点として扱われ、「クリーンシート」の計算に影響します。",
            "「クリーンシート」、「勝利」は、試合終了時に加算されます。",
          ],
        };
        return (
          <div>
            <div className="py-4">
              {howToPlayText.map((item, index) => (
                <RuleText key={index} {...item} />
              ))}
            </div>
            <ScoringLists />
            <div className="bg-light-blue px-4 sm:px-8 py-4">
              <MustReadTitle title="遊び方について" titleFontSize="18px" labelFontSize="18px" />

              <RuleText title="選手の評価方法" text={evaluationText.text} />
            </div>
          </div>
        );
      }
      case "prize":
        return (
          <div>
            <div className="py-8">
              <Prize round={round} />
            </div>
          </div>
        );

      default:
        return null; // Handle default case
    }
  };

  if (isRoundLoading || !selectedRound) {
    return (
      <div className="w-full h-screen flex justify-center items-center bg-blue">
        <LoadingSpinner />
      </div>
    );
  }

  const matchName = `${selectedRound.homeName} vs ${selectedRound.awayName}`;
  const roundDetailPlayButtonText = location.state?.roundDetailPlayButtonText;

  return (
    <div className="w-full bg-white pb-4">
      <div className="flex justify-center items-center p-[12px] bg-blue">
        <p className="font-bold text-xs whitespace-nowrap overflow-hidden text-primary text-center">{matchName}</p>
      </div>
      <div className="m-2">
        <RoundCard isRounded={false} round={selectedRound} onClick={handleGotoSelectPlayers} />
        <RoundDetailPlayButton
          roundDetailPlayButtonText={roundDetailPlayButtonText}
          handleGotoSelectPlayers={handleGotoSelectPlayers}
        />
        <div className="border-t border-t-[#ECECEC] border-b border-b-[#ADADAD]">
          <SelectDetailTab selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        </div>

        {isRoundLoading ? (
          <LoadingSpinner />
        ) : selectedRound ? (
          <div className="mx-2">
            <SelectedContent round={selectedRound} />
          </div>
        ) : (
          <ComingSoon />
        )}
      </div>
    </div>
  );
};

const RoundInfoText = () => {
  const data = [
    {
      title: "エントリーについて",
      text: [
        "一度エントリーを確定すると、選手の入れ替えや変更、キャンセルはできませんので、ご注意ください。",
        "選択した選手が試合に出場しなかった場合、その選手の活躍ポイント（FP）は0ポイントとなります。また、試合開始前に選手の出場不可が判明した場合でも、確定したエントリー内容は変更できません。",
        "各大会にはエントリーの締切時間が設定されています。締切時間を過ぎると、いかなる理由があってもエントリーはできませんので、事前に余裕をもってご参加ください。",
        "試合が中止・中断となった場合、エントリーに使用したポイントは返還されます。返還は無償ポイントのみで行われます。",
        "大会への参加には原則として無料ポイントを使用しますが、エントリー時に無料ポイントが不足している場合は、賞金を使用してエントリーすることも可能です。",
        "本サービスでは、公平なプレイ環境を維持するため、複数アカウントの使用、他人になりすます行為、システムの悪用などの不正行為を禁止しています。不正が発覚した場合は、エントリーの無効化、賞金受取資格の取り消し、アカウントの停止などの措置を取ることがあります。",
      ],
    },
    {
      title: "大会について",
      text: [
        "獲得した活躍ポイント（FP）が同点の場合、該当するプレイヤー全員が同じ順位となり、賞金は均等に分配されます。",
        "スコア計算には通常の試合時間に加えて延長戦のデータが反映されます。ただし、PK戦の成績は計算に含まれません。",
        "賞金総額および各順位の賞金額は大会ごとに異なります。エントリー前に、大会ごとの賞金設定や詳細ルールをご確認ください。",
        "各大会にはエントリーできる人数の上限が設定されています。上限に達していない場合、大会開始時間までエントリー可能です。大会開始時間までに定員に達しなかった場合でも、大会は予定通り開催され、賞金の変更はありません。",
        "本サービスの試合データは Sportradar（<a class='underline text-secondary' href='https://sportradar.com/?lang=en-us'>https://sportradar.com/?lang=en-us</a>） が提供するスポーツデータを使用しており、選手の活躍ポイントや大会結果もSportradarのデータを基に算出されます。そのため、実際の試合結果や選手の成績と異なる場合があっても、当社では責任を負いかねます。",
        "Sportradarがデータを確定した時点の情報が、最終的な大会の順位を決定するものとなります。通常、試合終了後10分程度で確定されますが、確定タイミングについては当社では保証できません。",
        "まれに、大会終了から数日後に公式スコアラーが統計を修正することがありますが、大会終了後に公式スコアが変更された場合でも、大会結果には影響しません。",
        "不正行為が確認された場合など、当社の判断により大会を中断することがあります。",
      ],
    },
  ];

  return (
    <div>
      {data.map((item, index) => (
        <RuleText key={index} title={item.title} text={item.text} />
      ))}
    </div>
  );
};

const RuleText = ({
  title,
  text,
  textClassName,
}: {
  title: string;
  text: string[];
  textClassName?: string;
}) => {
  return (
    <div>
      <p className="text-base font-bold py-2">
        <span className=" text-secondary"> ● </span>
        <span className="text-black">{title}</span>
      </p>

      <ol className={cn("list-disc pl-5 text-dark-gray text-sm", textClassName)}>
        {text.map((item, index) => (
          <li
            key={index}
            className="text-sm my-[2px]"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{ __html: item }}
          />
        ))}
      </ol>
    </div>
  );
};

const LoadingSpinner = () => {
  return (
    <div className="text-center">
      <div
        className={`
        h-8 w-8 border-4
        border-primary
        inline-block
        animate-spin 
        rounded-full 
        border-solid 
        border-r-transparent 
        align-[-0.125em] 
        motion-reduce:animate-[spin_1.5s_linear_infinite]
      `}
      >
        <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]" />
      </div>
      <div className="text-primary">Loading...</div>
    </div>
  );
};
